import { z } from "zod";
import {
  apiGet,
  apiPost,
  apiPut,
  apiRoutes,
  buildApiRoute,
  normalizePagedResult,
  toQueryParams,
} from "./entityApiShared.js";
import type { PageQuery, PagedResult } from "../types/api.types.js";
import { requireCmtsIdentifier, type ComplaintCreatePayload } from "../core/payloads/cmtsTypedPayloads.js";
import type {
  ComplaintAllowedActionDto,
  ComplaintCommentDto,
  ComplaintCreateRequest,
  ComplaintCreateResultDto,
  ComplaintCreateSchemaDto,
  ComplaintDetailDto,
  ComplaintEvidenceDto,
  ComplaintMessageDto,
  ComplaintSummaryDto,
  ComplaintTaskDto,
  ComplaintUpdateRequest,
  ComplaintWorkflowActionRequest,
  ComplaintWorkflowMutationResultDto,
  ComplaintTimelineItemDto,
} from "../../types/complaint.types.js";
import {
  complaintCommentSchema,
  complaintEvidenceSchema,
  complaintMessageSchema,
  complaintSummarySchema,
  complaintTaskSchema,
  parseComplaintAllowedActions,
  parseComplaintCreateResult,
  parseComplaintCreateSchema,
  parseComplaintDetail,
  parseComplaintTimeline,
  parseComplaintWorkflowResult,
} from "../../types/complaint.types.js";

export type ComplaintDto = ComplaintDetailDto;
export type WorkflowActionRequest = ComplaintWorkflowActionRequest;
export type ComplaintAllowedAction = ComplaintAllowedActionDto;
export type ComplaintTimelineDto = ComplaintTimelineItemDto;
export type { ComplaintCreateRequest, ComplaintUpdateRequest, ComplaintCommentDto, ComplaintTaskDto, ComplaintEvidenceDto };

export type ComplaintCommentCreateRequest = Readonly<{
  commentTypeId: string;
  visibilityId: string;
  message: string;
  parentCommentId?: string | undefined;
  evidenceDocumentId?: string | undefined;
  workflowStatusId?: string | undefined;
}>;

export type ComplaintTaskCreateRequest = Readonly<{
  title: string;
  description?: string | undefined;
  assignedToUserId?: string | undefined;
  assignedToRoleId?: string | undefined;
  statusId: string;
  dueAt?: string | undefined;
}>;

function publicComplaintCreatePayload(payload: ComplaintCreatePayload): Record<string, unknown> {
  const {
    tenantId: _tenantId,
    tenantCode: _tenantCode,
    createdByUserId: _createdByUserId,
    idempotencyKey: _idempotencyKey,
    clientMutationId: _clientMutationId,
    correlationId: _correlationId,
    ...businessFields
  } = payload as ComplaintCreatePayload & {
    readonly tenantId?: string;
    readonly tenantCode?: string;
    readonly createdByUserId?: string;
  };
  return businessFields;
}

function lifecycleActionPayload(actionCode: string, payload: WorkflowActionRequest): Record<string, unknown> {
  const reason = typeof payload.reason === "string" && payload.reason.trim() ? payload.reason.trim() : undefined;
  const comment = typeof payload.comment === "string" && payload.comment.trim() ? payload.comment.trim() : undefined;
  return {
    ...(reason ? { reason } : {}),
    ...(comment ? { comment } : {}),
    ...(payload.evidenceIds?.length ? { evidenceIds: payload.evidenceIds } : {}),
    ...(payload.targetOwnerId ? { targetOwnerId: payload.targetOwnerId } : {}),
    ...(payload.targetExpertId ? { targetExpertId: payload.targetExpertId } : {}),
    ...(payload.expectedRowVersion ? { expectedRowVersion: payload.expectedRowVersion } : {}),
    actionCode,
  };
}

function parseListResponse(input: unknown, query?: PageQuery): PagedResult<ComplaintSummaryDto> {
  const paged = normalizePagedResult<unknown>(input, query);
  return {
    ...paged,
    items: z.array(complaintSummarySchema).parse(paged.items),
  };
}

export const complaintApi = {
  list: async (query?: PageQuery, signal?: AbortSignal): Promise<PagedResult<ComplaintSummaryDto>> => {
    const response = await apiGet<unknown>(buildApiRoute(apiRoutes.complaints.list, {}, { query: toQueryParams(query) }), { signal });
    return parseListResponse(response, query);
  },

  get: async (id: string, signal?: AbortSignal): Promise<ComplaintDetailDto> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    return parseComplaintDetail(await apiGet<unknown>(buildApiRoute(apiRoutes.complaints.get, { id: complaintId }), { signal }));
  },

  create: async (payload: ComplaintCreateRequest, idempotencyKey?: string): Promise<ComplaintCreateResultDto> =>
    parseComplaintCreateResult(await apiPost<unknown>(buildApiRoute(apiRoutes.complaints.create), publicComplaintCreatePayload(payload), { idempotencyKey })),

  createSchema: async (signal?: AbortSignal): Promise<ComplaintCreateSchemaDto> =>
    parseComplaintCreateSchema(await apiGet<unknown>(buildApiRoute(apiRoutes.complaints.createSchema), { signal })),

  update: async (id: string, payload: ComplaintUpdateRequest, idempotencyKey?: string): Promise<ComplaintWorkflowMutationResultDto> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    return parseComplaintWorkflowResult(await apiPut<unknown>(buildApiRoute(apiRoutes.complaints.update, { id: complaintId }), lifecycleActionPayload("UPDATE", payload), { idempotencyKey }));
  },

  getAllowedActions: async (id: string, signal?: AbortSignal): Promise<ComplaintAllowedActionDto[]> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    return parseComplaintAllowedActions(await apiGet<unknown>(buildApiRoute(apiRoutes.complaints.allowedActions, { id: complaintId }), { signal })).actions;
  },

  getTimeline: async (id: string, signal?: AbortSignal): Promise<readonly ComplaintTimelineItemDto[]> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    return parseComplaintTimeline(await apiGet<unknown>(buildApiRoute(apiRoutes.complaints.timeline, { id: complaintId }), { signal }));
  },

  executeWorkflowAction: async (id: string, actionCode: string, payload: WorkflowActionRequest, idempotencyKey?: string): Promise<ComplaintWorkflowMutationResultDto> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    const workflowActionCode = requireCmtsIdentifier(actionCode, "actionCode");
    return parseComplaintWorkflowResult(await apiPost<unknown>(buildApiRoute(apiRoutes.complaints.workflowAction, { id: complaintId, actionCode: workflowActionCode }), lifecycleActionPayload(workflowActionCode, payload), { idempotencyKey }));
  },

  listComments: async (id: string, signal?: AbortSignal): Promise<readonly ComplaintCommentDto[]> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    return z.array(complaintCommentSchema).parse(await apiGet<unknown>(buildApiRoute(apiRoutes.complaints.comments.list, { id: complaintId }), { signal }));
  },

  createComment: async (id: string, payload: ComplaintCommentCreateRequest, idempotencyKey?: string): Promise<ComplaintCommentDto> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    return complaintCommentSchema.parse(await apiPost<unknown>(buildApiRoute(apiRoutes.complaints.comments.create, { id: complaintId }), payload, { idempotencyKey }));
  },

  listTasks: async (id: string, signal?: AbortSignal): Promise<readonly ComplaintTaskDto[]> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    return z.array(complaintTaskSchema).parse(await apiGet<unknown>(buildApiRoute(apiRoutes.complaints.tasks.list, { id: complaintId }), { signal }));
  },

  createTask: async (id: string, payload: ComplaintTaskCreateRequest, idempotencyKey?: string): Promise<ComplaintTaskDto> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    return complaintTaskSchema.parse(await apiPost<unknown>(buildApiRoute(apiRoutes.complaints.tasks.create, { id: complaintId }), payload, { idempotencyKey }));
  },

  listMessages: async (id: string, signal?: AbortSignal): Promise<readonly ComplaintMessageDto[]> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    return z.array(complaintMessageSchema).parse(await apiGet<unknown>(buildApiRoute(apiRoutes.complaints.messages.list, { id: complaintId }), { signal }));
  },

  listEvidence: async (id: string, signal?: AbortSignal): Promise<readonly ComplaintEvidenceDto[]> => {
    const complaintId = requireCmtsIdentifier(id, "complaintId");
    return z.array(complaintEvidenceSchema).parse(await apiGet<unknown>(buildApiRoute(apiRoutes.complaints.evidence.list, { id: complaintId }), { signal }));
  },
};
